/**
 * nh-gifts.js — "Paliers cadeaux" (gift tiers) + free delivery. See docs/nanous-house.md (ADR-026).
 *
 * Data: <script id="nh-gift-config" type="application/json"> printed once per page by
 * sections/nh-gift-tiers.liquid (metaobjects "Palier cadeau" + free-shipping threshold). Amounts in cents.
 *
 * 1. Cart sync: after every cart change (event nh:cart-changed from nh-cart.js) the cart is made to hold
 *    exactly the gifts earned: lines of 0 DH gift products carrying the hidden property _nh_gift = tier handle.
 *    Gifts of tiers no longer reached are removed. A gift product added any other way is removed.
 *    "Choose N" tiers start with the first N products as the default choice; the customer can swap them
 *    with the buttons (tapping another product replaces the most recent choice).
 * 2. <nh-gift-bar data-context="drawer|product">: same card on the product page and in the cart:
 *    message, steps, free-delivery line, choice buttons, "Voir tous les cadeaux" accordion;
 *    next-gift preview in the cart only. Redrawn on every cart change.
 * 3. Gift rows in the cart list get the class nh-gift-row (no quantity / remove, "Offert" label).
 * 4. Confetti + "Débloqué : …" message when the cart reaches a new step (not on page load); when several
 *    steps are reached at once (big add), one message per gift, one after the other. ADR-032/033.
 * 5. "Cumulables" line under the message: gifts add up, with the unlocked gifts as small pictures.
 * 6. Brands without gifts (setting "Marques sans cadeaux", e.g. Biomecanics): their products do not count
 *    toward the gift steps (they still count toward free delivery, like Shopify's shipping rate). Their
 *    product page shows a note + the free-delivery line only; the cart shows the note. ADR-044.
 * 7. "Cadeau membre" (sections/nh-member-gift.liquid, JSON #nh-member-config): for a signed-in customer
 *    (by default once per customer: never received it before) one more gift, key "__member", kept like a tier gift as soon as the
 *    cart holds an eligible product (and reaches the optional minimum). Not a step of the bar: it only
 *    adds its choice buttons, and confetti when it is added. Guests never keep it (removed as a stray).
 */

const configEl = document.getElementById('nh-gift-config');
const CFG = configEl ? JSON.parse(configEl.textContent) : null;
const memberEl = document.getElementById('nh-member-config');
const MEMBER = memberEl ? JSON.parse(memberEl.textContent) : null;
const MEMBER_KEY = '__member';
const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
const rawFetch = (...args) => (window.nhFetch || window.fetch)(...args);
const failed = new Set(); // variant ids Shopify refused to add (unpublished, sold out…)
let cart = null;
let queue = Promise.resolve();
let syncRuns = [];

const ICON = {
  gift: '<svg aria-hidden="true" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"><rect x="4" y="10" width="16" height="10" rx="1.5"/><path d="M3 7h18v3H3zM12 7v13M12 7c-2-4-6-3-5 0M12 7c2-4 6-3 5 0"/></svg>',
  truck: '<svg aria-hidden="true" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"><path d="M3 7h11v9H3zM14 10h4l3 3v3h-7"/><circle cx="7" cy="17.5" r="1.5"/><circle cx="17" cy="17.5" r="1.5"/></svg>',
  plus: '<svg aria-hidden="true" viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"><path d="M12 5v14M5 12h14"/></svg>',
  check: '<svg aria-hidden="true" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12.5l4.5 4.5L19 7.5"/></svg>',
};

const esc = (s) =>
  String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

/** Shop money format; whole amounts without decimals (350 DH, not 350,00 DH). */
function money(cents) {
  const format = CFG.moneyFormat || '{{amount}}';
  const kind = (format.match(/\{\{\s*(\w+)\s*\}\}/) || [null, 'amount'])[1];
  const comma = kind.indexOf('comma') >= 0;
  const decimals = kind.indexOf('no_decimals') < 0 && cents % 100 !== 0;
  let [int, dec] = (cents / 100).toFixed(decimals ? 2 : 0).split('.');
  int = int.replace(/\B(?=(\d{3})+(?!\d))/g, comma ? '.' : ',');
  return format.replace(/\{\{\s*\w+\s*\}\}/, dec ? int + (comma ? ',' : '.') + dec : int);
}

const isGift = (line) => !!(line.properties && line.properties._nh_gift);
const giftsOf = (c, key) => c.items.filter((l) => isGift(l) && l.properties._nh_gift === key);
const norm = (s) => String(s == null ? '' : s).trim().toLowerCase();
const EXCLUDED = new Set(((CFG && CFG.excludedVendors) || []).map(norm).filter(Boolean));
const isExcludedVendor = (vendor) => EXCLUDED.has(norm(vendor));
const sum = (lines) => lines.reduce((total, l) => total + l.final_line_price, 0);
/** Amount that counts toward the gift steps: no gifts, no products of brands without gifts. */
const eligibleTotal = (c) => sum(c.items.filter((l) => !isGift(l) && !isExcludedVendor(l.vendor)));
/** Amount that counts toward free delivery: everything except the gifts (as Shopify's shipping rate). */
const cartTotal = (c) => sum(c.items.filter((l) => !isGift(l)));
/** Amount used for a step: free delivery counts the whole cart, gift steps only eligible products. */
const progress = (s, c) => (s.shipping ? cartTotal(c) : eligibleTotal(c));
const reached = (s, c) => progress(s, c) >= s.amount;
/** Brands without gifts present in the cart, as written on the products ("Biomecanics"). */
const excludedIn = (c) => [...new Set(c.items.filter((l) => !isGift(l) && isExcludedVendor(l.vendor)).map((l) => l.vendor))];

/** All steps, sorted by amount: gift tiers that have an available product + the free-delivery step. */
function steps() {
  const list = (CFG.enabled ? CFG.tiers : [])
    .map((t) => ({ ...t, products: t.products.filter((p) => p.available && !failed.has(p.id)) }))
    .filter((t) => t.amount > 0 && t.products.length);
  if (CFG.shipping.amount > 0) {
    list.push({ key: '__shipping', shipping: true, amount: CFG.shipping.amount, title: CFG.shipping.title, message: CFG.shipping.message, products: [] });
  }
  return list.sort((a, b) => a.amount - b.amount);
}

/** Member gift as a tier, or null when the customer isn't eligible or no gift is available. */
function memberTier() {
  if (!MEMBER || !MEMBER.active) return null;
  const products = (MEMBER.products || []).filter((p) => p.available && !failed.has(p.id));
  if (!products.length) return null;
  return { key: MEMBER_KEY, member: true, amount: MEMBER.amount || 0, pick: MEMBER.pick || 0, title: MEMBER.title, message: MEMBER.title, image: '', products };
}

/** Gift tiers + member gift (no free-delivery step). */
const giftTiers = () => [...steps().filter((s) => !s.shipping), memberTier()].filter(Boolean);

/** A gift tier is earned when the eligible amount reaches it (member gift at 0: as soon as there is an eligible product). */
const earned = (t, c) => {
  const total = eligibleTotal(c);
  return total >= t.amount && total > 0;
};

/* ---------- 1. Cart sync ---------- */

function plan(c) {
  const allGiftIds = new Set([...CFG.tiers, MEMBER || { products: [] }].flatMap((t) => (t.products || []).map((p) => p.id)));
  const updates = {};
  const add = [];
  const keep = new Set();

  giftTiers()
    .filter((t) => earned(t, c))
    .forEach((t) => {
      const ids = t.products.map((p) => p.id);
      const wanted = t.pick > 0 ? Math.min(t.pick, ids.length) : ids.length;
      const seen = new Set();
      const chosen = giftsOf(c, t.key)
        .filter((l) => ids.includes(l.variant_id) && !seen.has(l.variant_id) && seen.add(l.variant_id))
        .slice(0, wanted);
      chosen.forEach((l) => {
        keep.add(l.key);
        if (l.quantity !== 1) updates[l.key] = 1;
      });
      // Missing gifts: all of them (pick 0), or the first products as default choice (pick N).
      ids
        .filter((id) => !seen.has(id))
        .slice(0, wanted - chosen.length)
        .forEach((id) => add.push({ id, quantity: 1, properties: { _nh_gift: t.key } }));
    });

  c.items.forEach((l) => {
    const stray = !isGift(l) && allGiftIds.has(l.variant_id);
    if ((isGift(l) && !keep.has(l.key)) || stray) updates[l.key] = 0;
  });
  return { add, updates };
}

async function post(path, body) {
  try {
    const res = await rawFetch(`${ROOT}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    return res.ok;
  } catch (e) {
    return false;
  }
}

async function afterChange() {
  if (window.nhRefreshCart) await window.nhRefreshCart();
  if (window.nhRenderDrawer) await window.nhRenderDrawer();
}

function sync() {
  queue = queue
    .then(async () => {
      if (!cart) return;
      const first = plan(cart);
      if (!first.add.length && !Object.keys(first.updates).length) return;
      // Safety net: never loop if Shopify keeps refusing a change silently.
      const now = Date.now();
      syncRuns = syncRuns.filter((t) => now - t < 10000);
      if (syncRuns.length >= 6) return;
      syncRuns.push(now);
      // Let Horizon finish its own update (e.g. a line being removed and redrawn) first,
      // then work from the cart as it is now.
      if (window.nhWaitForHorizon) await window.nhWaitForHorizon();
      cart = await rawFetch(`${ROOT}cart.js`).then((r) => r.json());
      const p = plan(cart);
      let changed = false;
      if (Object.keys(p.updates).length) changed = (await post('cart/update.js', { updates: p.updates })) || changed;
      for (const item of p.add) {
        if (await post('cart/add.js', { items: [item] })) changed = true;
        else failed.add(item.id);
      }
      if (changed) await afterChange();
    })
    .catch(() => {});
  return queue;
}

/** Choice buttons: swap a gift. The tier is always full (default choice), so the most recent choice
 *  leaves and the tapped product comes in. Tapping a gift already chosen changes nothing. */
function choose(tierKey, variantId) {
  queue = queue
    .then(async () => {
      if (window.nhWaitForHorizon) await window.nhWaitForHorizon();
      const tier = giftTiers().find((s) => s.key === tierKey);
      if (!tier || !cart || !earned(tier, cart)) return redraw();
      const mine = giftsOf(cart, tier.key);
      const current = mine.find((l) => l.variant_id === variantId);
      if (current) return redraw();
      if (mine.length >= Math.max(tier.pick, 1)) {
        await post('cart/update.js', { updates: { [mine[mine.length - 1].key]: 0 } });
      }
      if (!(await post('cart/add.js', { items: [{ id: variantId, quantity: 1, properties: { _nh_gift: tier.key } }] }))) {
        failed.add(variantId);
      }
      await afterChange();
    })
    .catch(redraw);
}

/** Redraws the bars with the current cart (re-enables the buttons when nothing changed). */
function redraw() {
  if (cart) document.dispatchEvent(new CustomEvent('nh:cart-changed', { detail: cart }));
}

/* ---------- 2. <nh-gift-bar> ---------- */

/** Picture of a step: tier image, else the chosen gift(s), else the first gift product. */
function stepImage(s, c) {
  if (s.image) return s.image;
  const chosen = c ? giftsOf(c, s.key).map((l) => s.products.find((p) => p.id === l.variant_id)).filter(Boolean) : [];
  const p = chosen[0] || s.products[0];
  return p ? p.image : '';
}

/** "Cumulables" line: shows that gifts add up, and which ones are already unlocked. */
function stackView(c, gifts, total) {
  const T = CFG.texts;
  if (!T.cumulBadge && !T.cumulNone) return '';
  const unlocked = gifts.filter((s) => total >= s.amount);
  const text = !unlocked.length ? T.cumulNone : unlocked.length === 1 ? T.cumulOne : T.cumulMany.replace('[count]', unlocked.length);
  const thumbs = unlocked
    .slice(-4)
    .map((s) => stepImage(s, c))
    .filter(Boolean)
    .map((src) => `<img src="${esc(src)}" alt="" width="28" height="28" loading="lazy">`)
    .join('');
  return `<div class="nh-gift__stack">${T.cumulBadge ? `<span class="nh-gift__stack-pill">${ICON.plus}${esc(T.cumulBadge)}</span>` : ''}<span class="nh-gift__stack-text">${esc(text || '')}</span>${thumbs ? `<span class="nh-gift__stack-thumbs">${thumbs}</span>` : ''}</div>`;
}

function view(c, context, el) {
  const T = CFG.texts;
  const all = steps();
  const gifts = all.filter((s) => !s.shipping);
  const total = eligibleTotal(c);
  const next = all.find((s) => !reached(s, c));
  const n = all.length;
  const ship = CFG.shipping;
  const shipTotal = cartTotal(c);
  const shipRemaining = el.dataset.shipRemaining || ship.remaining || '';
  const shipReached = el.dataset.shipReached || ship.reached || '';
  const shipLine = () =>
    shipTotal >= ship.amount ? esc(shipReached) : esc(shipRemaining).replace('[amount]', `<strong>${money(ship.amount - shipTotal)}</strong>`);
  const shipHtml = ship.amount > 0 ? `<p class="nh-gift__ship">${ICON.truck}<span>${shipLine()}</span></p>` : '';

  // Brands without gifts: a note in the cart when such products are in it, and on their own product page
  // only the note + the free-delivery line (no steps that this product would not move).
  const note = (brands) =>
    brands.length && T.excluded ? `<p class="nh-gift__note">${esc(T.excluded.replace('[brand]', brands.join(' / ')))}</p>` : '';
  const pageVendor = context === 'product' && el.dataset.vendor && isExcludedVendor(el.dataset.vendor) ? el.dataset.vendor : '';
  if (pageVendor && gifts.length) return `${note([pageVendor])}${shipHtml}`;
  const noteHtml = gifts.length ? note(excludedIn(c)) : '';

  let fill = 100;
  for (let i = 0; i < n; i++) {
    const prev = i ? all[i - 1].amount : 0;
    if (!reached(all[i], c)) {
      const part = Math.min(Math.max((progress(all[i], c) - prev) / (all[i].amount - prev), 0), 1);
      fill = ((i + part) / n) * 100;
      break;
    }
  }

  let message;
  if (!gifts.length) message = shipLine();
  else if (next) message = esc(T.remaining).replace('[gift]', esc(next.message)).replace('[amount]', `<strong>${money(next.amount - progress(next, c))}</strong>`);
  else message = esc(T.done);

  const stepsHtml = all
    .map((s, i) => {
      const state = reached(s, c) ? ' is-done' : s === next ? ' is-next' : '';
      return `<li class="nh-gift__step${state}" style="--pos:${((i + 1) / n) * 100}%"><span class="nh-gift__dot">${s.shipping ? ICON.truck : ICON.gift}</span><span class="nh-gift__amount">${money(s.amount)}</span><span class="visually-hidden">${esc(s.title)}</span></li>`;
    })
    .join('');

  let nextHtml = '';
  if (context === 'drawer' && next) {
    const img = next.image || (next.products[0] && next.products[0].image);
    const media = next.shipping
      ? `<span class="nh-gift__next-media nh-gift__next-media--icon">${ICON.truck}</span>`
      : img
        ? `<img class="nh-gift__next-media" src="${esc(img)}" alt="" width="44" height="44" loading="lazy">`
        : '';
    nextHtml = `<div class="nh-gift__next">${media}<div><span class="nh-gift__next-label">${esc(T.next)} · ${money(next.amount)}</span><span class="nh-gift__next-title">${esc(next.title)}</span></div></div>`;
  }

  // Choice buttons (product page and cart) for every reached "choose N" tier.
  const chooseHtml = [...gifts, memberTier()]
    .filter((s) => s && s.pick > 0 && earned(s, c))
    .map((s) => {
      const picked = new Set(giftsOf(c, s.key).map((l) => l.variant_id));
      const wanted = Math.min(s.pick, s.products.length);
      const label = s.pick > 1 ? T.chooseMany.replace('[count]', s.pick) : T.choose;
      const count = s.pick > 1 ? ` (${picked.size}/${wanted})` : '';
      const options = s.products
        .map(
          (p) =>
            `<button type="button" class="nh-gift__option" data-tier="${esc(s.key)}" data-id="${p.id}" aria-pressed="${picked.has(p.id)}">${p.image ? `<img src="${esc(p.image)}" alt="" width="40" height="40" loading="lazy">` : ''}<span>${esc(p.title)}</span></button>`
        )
        .join('');
      return `<div class="nh-gift__choose${picked.size < wanted ? ' nh-gift__choose--todo' : ''}"><p class="nh-gift__choose-title">${esc(label)}${count} <span>· ${esc(s.title)}</span></p><div class="nh-gift__options">${options}</div></div>`;
    })
    .join('');


  // "Voir tous les cadeaux": accordion listing every step and its status.
  let allHtml = '';
  if (gifts.length && T.link) {
    const items = all
      .map((s) => {
        const done = reached(s, c);
        const img = stepImage(s, c);
        const media = s.shipping
          ? `<span class="nh-gift__item-media nh-gift__item-media--ship">${ICON.truck}</span>`
          : img
            ? `<img class="nh-gift__item-media" src="${esc(img)}" alt="" width="44" height="44" loading="lazy">`
            : `<span class="nh-gift__item-media">${ICON.gift}</span>`;
        const status = done ? `${ICON.check}${esc(T.unlocked)}` : esc(T.missing).replace('[amount]', money(s.amount - progress(s, c)));
        return `<li class="nh-gift__item${done ? ' is-done' : ''}">${media}<span class="nh-gift__item-text"><span class="nh-gift__item-title">${esc(s.title)}</span><span class="nh-gift__item-amount">${money(s.amount)}</span></span><span class="nh-gift__item-status">${status}</span></li>`;
      })
      .join('');
    allHtml = `<details class="nh-gift__all"><summary>${esc(T.link)}</summary><ul class="nh-gift__list" role="list">${items}</ul></details>`;
  }

  const stackHtml = gifts.length ? stackView(c, gifts, total) : '';

  return `<p class="nh-gift__msg" aria-live="polite"><span class="nh-gift__msg-icon">${gifts.length ? ICON.gift : ICON.truck}</span><span>${message}</span></p>${noteHtml}${stackHtml}<div class="nh-gift__track" style="--fill:${fill}%"><span class="nh-gift__fill"></span><ol class="nh-gift__steps" role="list">${stepsHtml}</ol></div>${nextHtml}${chooseHtml}${gifts.length ? shipHtml : ''}${allHtml}`;
}

class NhGiftBar extends HTMLElement {
  connectedCallback() {
    this.onCart = (e) => {
      cart = e.detail; // never draw an older cart, whatever the listener order
      this.render();
    };
    document.addEventListener('nh:cart-changed', this.onCart);
    document.addEventListener('nh:cart-redrawn', this.onCart); // drawer redrawn by Horizon: draw again
    this.addEventListener('click', (e) => {
      const button = e.target.closest('.nh-gift__option');
      if (!button) return;
      this.querySelectorAll('.nh-gift__option').forEach((b) => (b.disabled = true));
      choose(button.dataset.tier, Number(button.dataset.id));
    });
    this.addEventListener('toggle', (e) => {
      if (e.target.classList && e.target.classList.contains('nh-gift__all')) this.listOpen = e.target.open;
    }, true);
    this.render();
  }
  disconnectedCallback() {
    document.removeEventListener('nh:cart-changed', this.onCart);
    document.removeEventListener('nh:cart-redrawn', this.onCart);
  }
  render() {
    if (!cart) return;
    if (!steps().length) {
      this.hidden = true;
      return;
    }
    this.innerHTML = view(cart, this.dataset.context || 'drawer', this);
    const list = this.querySelector('.nh-gift__all');
    if (list && this.listOpen) list.open = true;
    this.hidden = false;
  }
}

/* ---------- 4. Confetti when a new step is reached ---------- */

let reachedKeys = null; // null until the first cart of the page: no confetti on page load
const CONFETTI_COLORS = ['#0F4C5C', '#C97B93', '#B03A5B', '#E3EEEF', '#F6E3EA', '#A4546F'];

function celebrate(c) {
  const member = memberTier();
  const now = steps().filter((s) => reached(s, c));
  if (member && earned(member, c) && giftsOf(c, MEMBER_KEY).length) now.push(member);
  const fresh = reachedKeys ? now.filter((s) => !reachedKeys.has(s.key)) : [];
  // Several steps at once: one message (and confetti) per gift, one after the other.
  if (CFG.confetti !== false) fresh.forEach((s, i) => setTimeout(() => confetti(s), i * 1500));
  reachedKeys = new Set(now.map((s) => s.key));
}

/** Confetti + pill "Débloqué : <gift>" (with its picture) above the visible gift bar. */
function confetti(step) {
  const visible = (el) => el.getClientRects().length > 0;
  const bars = [...document.querySelectorAll('nh-gift-bar')].filter(visible);
  const anchor = bars.find((b) => b.closest('dialog[open]')) || bars[0];
  const dialog = anchor && anchor.closest('dialog[open]');
  // Inside the open cart drawer (top layer) the layer must live in the dialog to be seen.
  const host = dialog || document.body;
  const box = host === document.body ? { left: 0, top: 0 } : host.getBoundingClientRect();
  const r = anchor ? anchor.getBoundingClientRect() : { left: window.innerWidth / 2, top: window.innerHeight / 3, width: 0 };
  const x = r.left + r.width / 2 - box.left;
  const y = Math.max(r.top - box.top, 0) + 24;

  const layer = document.createElement('div');
  layer.className = 'nh-confetti' + (dialog ? ' nh-confetti--in-dialog' : '');
  layer.setAttribute('aria-hidden', 'true');
  for (let i = 0; i < 40; i++) {
    const p = document.createElement('i');
    p.style.cssText = `left:${x}px;top:${y}px;--dx:${Math.round((Math.random() - 0.5) * 340)}px;--dy:${-Math.round(70 + Math.random() * 150)}px;--rot:${Math.round(Math.random() * 720 - 360)}deg;--c:${CONFETTI_COLORS[i % CONFETTI_COLORS.length]};animation-delay:${Math.round(Math.random() * 120)}ms`;
    layer.appendChild(p);
  }
  const hostWidth = host === document.body ? window.innerWidth : host.getBoundingClientRect().width;
  const toast = document.createElement('span');
  toast.className = 'nh-confetti__toast';
  toast.style.cssText = `left:${Math.min(Math.max(x, 140), hostWidth - 140)}px;top:${Math.max(y - 16, 8)}px`;
  const img = step.shipping ? '' : stepImage(step, cart);
  toast.innerHTML = `${step.shipping ? ICON.truck : img ? `<img src="${esc(img)}" alt="" width="24" height="24">` : ICON.gift}<span>${esc(CFG.texts.unlocked)} : ${esc(step.title)}</span>`;
  layer.appendChild(toast);
  host.appendChild(layer);
  setTimeout(() => layer.remove(), 2400);
}

/* ---------- 3. Gift rows in the cart list ---------- */

function markRows() {
  if (!cart) return;
  const giftKeys = new Set(cart.items.filter(isGift).map((l) => l.key));
  document.querySelectorAll('tr[data-key]').forEach((row) => {
    const on = giftKeys.has(row.dataset.key);
    row.classList.toggle('nh-gift-row', on);
    if (on) row.style.setProperty('--nh-gift-label', JSON.stringify(CFG.texts.badge || ''));
  });
}

if (CFG && !customElements.get('nh-gift-bar')) {
  // Store the new cart BEFORE the bars redraw: this listener must be registered before
  // customElements.define(), which connects the bars and adds their own listeners.
  document.addEventListener('nh:cart-changed', (e) => {
    cart = e.detail;
    celebrate(cart);
    markRows();
    sync();
  });
  customElements.define('nh-gift-bar', NhGiftBar);
  let timer;
  new MutationObserver(() => {
    clearTimeout(timer);
    timer = setTimeout(markRows, 40);
  }).observe(document.body, { childList: true, subtree: true });
  if (window.nhRefreshCart) window.nhRefreshCart();
}
